import type { FastFeedDailyRow } from "@/api/types";
import { Panel } from "@/components/ui";
import { useChartColors } from "@/lib/theme";
import { useECharts } from "@/lib/use-echarts";
import type { EChartsOption } from "echarts";
import { useMemo } from "react";

function ChartInner({
  rows,
  cc,
}: { rows: FastFeedDailyRow[]; cc: ReturnType<typeof useChartColors> }) {
  const newPct = rows.map((r) => {
    const total = r.new_buyer_count + r.returning_buyer_count;
    return total > 0 ? +((r.new_buyer_count / total) * 100).toFixed(1) : 100;
  });

  const option = useMemo<EChartsOption>(
    () => ({
      grid: { left: 48, right: 48, top: 32, bottom: 32 },
      legend: {
        data: ["Unique Buyers", "New Buyer %"],
        textStyle: { color: cc.text, fontSize: 10 },
        top: 6,
        right: 8,
      },
      xAxis: {
        type: "category",
        data: rows.map((r) => r.date),
        axisLabel: { fontSize: 10, color: cc.text },
        axisLine: { lineStyle: { color: cc.grid } },
        axisTick: { show: false },
      },
      yAxis: [
        {
          type: "value",
          name: "Buyers",
          nameTextStyle: { fontSize: 9, color: cc.text },
          minInterval: 1,
          axisLabel: { fontSize: 10, color: cc.text },
          splitLine: { lineStyle: { color: cc.grid, type: "dashed" } },
        },
        {
          type: "value",
          name: "%",
          nameTextStyle: { fontSize: 9, color: cc.text },
          min: 0,
          max: 100,
          axisLabel: { fontSize: 10, color: cc.text, formatter: (v: number) => `${v}%` },
          splitLine: { show: false },
        },
      ],
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        formatter: (p: unknown) => {
          const params = p as Array<{ seriesName: string; name: string; value: number }>;
          return `<b>${params[0].name}</b><br/>${params
            .map((s) =>
              s.seriesName === "New Buyer %"
                ? `${s.seriesName}: ${s.value}%`
                : `${s.seriesName}: ${s.value}`,
            )
            .join("<br/>")}`;
        },
      },
      series: [
        {
          name: "Unique Buyers",
          type: "bar",
          yAxisIndex: 0,
          data: rows.map((r) => r.unique_buyers),
          barMaxWidth: 28,
          itemStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "#a78bfa" },
                { offset: 1, color: "rgba(167,139,250,0.3)" },
              ],
            },
          },
        },
        {
          name: "New Buyer %",
          type: "line",
          yAxisIndex: 1,
          data: newPct,
          symbol: rows.length <= 5 ? "circle" : "none",
          symbolSize: 5,
          lineStyle: { color: "#fb923c", width: 2, type: "dashed" },
          itemStyle: { color: "#fb923c" },
        },
      ],
    }),
    [rows, cc, newPct],
  );

  const ref = useECharts(option, [option]);
  return <div ref={ref} style={{ height: 220 }} />;
}

interface Props {
  rows: FastFeedDailyRow[];
  loading?: boolean;
}

export function FastFeedBuyersChart({ rows, loading = false }: Props) {
  const cc = useChartColors();
  return (
    <Panel title="BUYER ACTIVITY">
      {loading ? (
        <div className="skeleton" style={{ height: 220, borderRadius: "var(--radius-panel)" }} />
      ) : rows.length === 0 ? (
        <div
          style={{
            height: 220,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--color-text-muted)",
            fontSize: 12,
          }}
        >
          No data in this range
        </div>
      ) : (
        <ChartInner rows={rows} cc={cc} />
      )}
    </Panel>
  );
}
